/* eslint-disable import/no-cycle */
/* eslint class-methods-use-this: ["error", { "exceptMethods": ["downloadInvoicePdfPrivate", "openInvoicePdfInNewTab"] }] */
import { action, makeObservable, observable, runInAction } from 'mobx';
import { saveAs } from 'file-saver';
import BaseStore from './base-stores/BaseStore';
import { RootStore } from './RootStore';
import {
  InvoiceInfo,
  InvoiceInfoPaginatedList,
  InvoiceService,
  InvoiceType,
  PaymentCardPaymentInfo,
  PaymentCardPaymentInfoPaginatedList,
  TransactionService
} from '../swagger/api';
import { MKInvoiceStatus } from '../utils/invoiceStatuses';

export enum PaymentTab {
  InvoiceTab = 1,
  CardPaymentTab = 2
}

export default class PaymentStore extends BaseStore {
  invoices: InvoiceInfo[] = [];

  cardPayments: PaymentCardPaymentInfo[] = [];

  pageSize: number = 0;

  numberOfInvoices: number = 0;

  filteredNumberOfInvoices: number = 0;

  numberOfCardPayments: number = 0;

  filteredNumberOfCardPayments: number = 0;

  hasInvoices: boolean = false;

  hasCardPayments: boolean = false;

  invoicePageNumber: number = 1;

  cardPaymentPageNumber: number = 1;

  activeTab: number = 1;

  setHasInvoicesToTrue = () => {
    this.hasInvoices = true;
  };

  setHasCardPaymentstoTrue = () => {
    this.hasCardPayments = true;
  };

  setInvoicePageNumber = (pageNumber: number) => {
    this.invoicePageNumber = pageNumber;
  };

  setCardPageNumber = (pageNumber: number) => {
    this.cardPaymentPageNumber = pageNumber;
  };

  setPaymentTab = (activeTab: number) => {
    this.activeTab = activeTab;
  };

  // The API only supports filtering by InvoiceStatus, not InvoiceType.
  // Credit invoices share statuses with non-credit invoices, so we filter
  // client-side by InvoiceType (CreditNote/PaymentVoucher) when the credit
  // filter is active. This means filteredNumberOfInvoices only reflects the
  // current page, so pagination may be inaccurate for credit-filtered results.
  // A backend InvoiceTypeList filter would resolve this.
  private isCreditFilterActive = (): boolean => {
    const { activeFilters } = this.rootStore.filterStore.invoiceFilterStore;
    return activeFilters.invoiceStatus.some((f) => f.filter === MKInvoiceStatus.Credit);
  };

  getInvoices = () => {
    this.rootStore.filterStore.invoiceFilterStore.setRequestParams();
    const params = this.rootStore.filterStore.invoiceFilterStore.requestParams;
    params.accountId = this.rootStore.userStore.currentAccount?.id!;
    const promise = InvoiceService.getInvoiceInfo(params);
    promise.then((val: InvoiceInfoPaginatedList) => {
      runInAction(() => {
        let items = val.paginatedItems!;
        if (this.isCreditFilterActive()) {
          items = items.filter(
            (i) =>
              i.invoiceType === InvoiceType.CreditNote ||
              i.invoiceType === InvoiceType.PaymentVoucher
          );
        }
        this.invoices = items;
        this.pageSize = val.pageSize!;
        this.numberOfInvoices = this.isCreditFilterActive() ? items.length : val.totalCount!;
        this.filteredNumberOfInvoices = this.isCreditFilterActive()
          ? items.length
          : val.filteredCount!;
      });
      // Workaround for API: Tab visibility should also count when API returns totalCount=0 but filteredCount has data.
      // Use hasInvoiceData (totalCount OR filteredCount > 0) instead of only numberOfInvoices > 0 for setHasInvoicesToTrue().
      const hasInvoiceData = (val.totalCount ?? 0) > 0 || (val.filteredCount ?? 0) > 0;
      if (hasInvoiceData) this.setHasInvoicesToTrue();
    });

    return promise;
  };

  getCardPayments = () => {
    this.rootStore.filterStore.cardPaymentFilterStore.setRequestParams();
    const params = this.rootStore.filterStore.cardPaymentFilterStore.requestParams;
    params.accountId = this.rootStore.userStore.currentAccount?.id!;
    const promise = InvoiceService.getCardPaymentsByAccountIdAsync(params);
    promise.then((val: PaymentCardPaymentInfoPaginatedList) => {
      runInAction(() => {
        this.cardPayments = val.paginatedItems!;
        this.pageSize = val.pageSize!;
        this.numberOfCardPayments = val.totalCount!;
        this.filteredNumberOfCardPayments = val.filteredCount!;
      });
      if (this.numberOfCardPayments > 0) this.setHasCardPaymentstoTrue();
    });

    return promise;
  };

  downloadInvoicePdfPrivate = (invoiceId: string, invoiceNumber: string) => {
    const invoiceName = this.rootStore.contentStore.cmsStore.getContent('Invoice')?.content;
    const promise = InvoiceService.getInvoicePdf({ invoiceId }, { responseType: 'blob' }).then(
      (response: any) => {
        saveAs(response, `${invoiceName} ${invoiceNumber}.pdf`);
      }
    );
    return promise;
  };

  openInvoicePdfInNewTab = (invoiceId: string) => {
    const promise = InvoiceService.getInvoicePdf({ invoiceId }, { responseType: 'blob' }).then(
      (response: any) => {
        const blobUrl = URL.createObjectURL(response);
        window.open(blobUrl, '_blank');
        // Cleanup blob URL after 5 seconds to free memory
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      }
    );
    return promise;
  };

  downloadInvoicePdf = (invoiceId: string, invoiceNumber: string) => {
    const params = {
      accountId: this.rootStore.userStore.currentAccount?.id!,
      invoiceId
    };
    const invoiceName = this.rootStore.contentStore.cmsStore.getContent('Invoice')?.content;
    const promise = InvoiceService.downloadInvoice(params, { responseType: 'blob' });
    promise.then((response: any) => {
      saveAs(response, `${invoiceName} ${invoiceNumber}.pdf`);
    });
  };

  downloadInvoiceCSV = (invoiceId: string, invoiceNumber: string) => {
    const params = {
      accountId: this.rootStore.userStore.currentAccount?.id!,
      invoiceId
    };
    const invoiceName = this.rootStore.contentStore.cmsStore.getContent('Invoice')?.content;
    const promise = TransactionService.downloadTransactionsForInvoicePeriodAsCsv(params, {
      responseType: 'blob'
    });
    promise.then((response: any) => {
      saveAs(response, `${invoiceName} ${invoiceNumber}.csv`);
    });
  };

  downloadInvoiceExcel = (invoiceId: string, invoiceNumber: string) => {
    const params = {
      accountId: this.rootStore.userStore.currentAccount?.id!,
      invoiceId
    };
    const invoiceName = this.rootStore.contentStore.cmsStore.getContent('Invoice')?.content;
    const promise = TransactionService.downloadTransactionsForInvoicePeriodAsExcel(params, {
      responseType: 'blob'
    });
    promise.then((response: any) => {
      saveAs(response, `${invoiceName} ${invoiceNumber}.xlsx`);
    });
  };

  downloadReminderLetter = (dunningId: string) => {
    const dunningNoticeNumberParams = {
      accountId: this.rootStore.userStore.currentAccount?.id!,
      dunningId
    };
    const reminderLetterName =
      this.rootStore.contentStore.cmsStore.getContent('ReminderLetter')?.content;
    const dunningNoticeNumberPromise =
      InvoiceService.getDunningNoticeNumberFromLatestDunningStepByDunningId(
        dunningNoticeNumberParams
      );
    dunningNoticeNumberPromise.then((dunningNoticeNumber) => {
      const reminderLetterPdfParams = {
        accountId: this.rootStore.userStore.currentAccount?.id!,
        dunningNoticeNumber
      };
      const promise = InvoiceService.downloadReminderLetter(reminderLetterPdfParams, {
        responseType: 'blob'
      });
      promise.then((response: any) => {
        saveAs(response, `${reminderLetterName} ${dunningNoticeNumber}.pdf`);
      });
    });
  };

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      invoices: observable,
      cardPayments: observable,
      numberOfInvoices: observable,
      filteredNumberOfInvoices: observable,
      numberOfCardPayments: observable,
      filteredNumberOfCardPayments: observable,
      invoicePageNumber: observable,
      cardPaymentPageNumber: observable,
      hasInvoices: observable,
      hasCardPayments: observable,
      setHasInvoicesToTrue: action,
      setHasCardPaymentstoTrue: action,
      activeTab: observable,
      getInvoices: action,
      setInvoicePageNumber: action,
      setCardPageNumber: action,
      setPaymentTab: action,
      downloadInvoicePdf: action,
      downloadInvoicePdfPrivate: action,
      openInvoicePdfInNewTab: action,
      downloadReminderLetter: action,
      downloadInvoiceCSV: action
    });
  }
}
